import React from "react";

import { CAMPAIGN_INK_HEX } from "../constants/campaignTheme";
import {
  SPOKE_FIELD_WHEELS,
  SPOKE_WHEEL_CENTRE,
  SPOKE_WHEEL_HUB_RADIUS,
  SPOKE_WHEEL_HUB_WIDTH,
  SPOKE_WHEEL_INNER_RIM_RADIUS,
  SPOKE_WHEEL_LINE_WIDTH,
  SPOKE_WHEEL_OUTER_RIM_RADIUS,
  SPOKE_WHEEL_TYRE_WIDTH,
  SPOKE_WHEEL_VIEWBOX_SIZE,
} from "../constants/spokeField";
import { SpokeWheel } from "../types/SpokeWheel";
import { resolveSpokeLines, resolveWheelPlacement } from "../utils/spokeWheelGeometry";

interface SpokeWheelArtProps {
  wheel: SpokeWheel;
}

const SpokeWheelArt: React.FC<SpokeWheelArtProps> = ({ wheel }) => (
  <svg
    viewBox={`0 0 ${SPOKE_WHEEL_VIEWBOX_SIZE} ${SPOKE_WHEEL_VIEWBOX_SIZE}`}
    className="absolute"
    style={resolveWheelPlacement({ wheel: wheel })}
    aria-hidden="true"
    focusable="false"
  >
    <g
      fill="none"
      stroke={CAMPAIGN_INK_HEX}
      strokeWidth={SPOKE_WHEEL_LINE_WIDTH}
      strokeLinecap="round"
    >
      {resolveSpokeLines({ spokeCount: wheel.spokeCount }).map((spokeLine) => (
        <line
          key={`${wheel.name}-${spokeLine.x2}-${spokeLine.y2}`}
          x1={spokeLine.x1}
          y1={spokeLine.y1}
          x2={spokeLine.x2}
          y2={spokeLine.y2}
        />
      ))}
      <circle cx={SPOKE_WHEEL_CENTRE} cy={SPOKE_WHEEL_CENTRE} r={SPOKE_WHEEL_INNER_RIM_RADIUS} />
      <circle
        cx={SPOKE_WHEEL_CENTRE}
        cy={SPOKE_WHEEL_CENTRE}
        r={SPOKE_WHEEL_OUTER_RIM_RADIUS}
        strokeWidth={SPOKE_WHEEL_TYRE_WIDTH}
      />
      <circle
        cx={SPOKE_WHEEL_CENTRE}
        cy={SPOKE_WHEEL_CENTRE}
        r={SPOKE_WHEEL_HUB_RADIUS}
        strokeWidth={SPOKE_WHEEL_HUB_WIDTH}
      />
    </g>
  </svg>
);

/**
 * The backdrop the charity surface stands on: two bicycle wheels, hubs off
 * opposite corners, drawn faintly enough to read as texture.
 *
 * Fixed to the viewport rather than to the page, so the content scrolls across
 * the wheels instead of dragging them along — a backdrop that scrolls with the
 * page is just a very large illustration at the top of it.
 *
 * The wrapper clips: both wheels deliberately overhang their corners, and
 * without `overflow-hidden` that overhang is content the page can be scrolled
 * sideways to reach.
 *
 * @returns The backdrop, or nothing a reader or a screen reader can reach
 */
export const CharitySpokeField: React.FC = () => (
  <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
    {SPOKE_FIELD_WHEELS.map((wheel) => (
      <SpokeWheelArt key={wheel.name} wheel={wheel} />
    ))}
  </div>
);
